import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  PermissionKeyString,
  PermissionStatus,
  PlanPermissions,
  Reason,
} from "data/models";
import { fetchPermissionsAdmin } from "data/queries-admin";
import { fetchPermissions } from "data/queries-common";
import { useQuery } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";
import { AnyPermissionsKey } from "./permission-keys";
import {
  PermissionMessageTemplateFn,
  PermissionReasonMessages,
  PermissionReasonMessagesString,
  PermissionsReasons,
} from "./permission-messages";

export interface usePermissionProps {
  permissionSets?: string[];
  organisationId?: string;
}

/**
 * A collection of permissions utility functions that allows you to check if the user has access
 *  for the provided permission keys.
 * @param permission_sets - Optional array of permission keys. If provided, overrides
 * the user's own permissions and uses the given set instead. ie ["professional_2025", "externally_managed"]
 */
export const usePermissions = (props?: usePermissionProps) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const queryKey = [
    QueryKeys.permissions,
    identityId,
    props?.organisationId,
    props?.permissionSets ?? [],
  ];

  const permissionsQuery = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      props?.organisationId != undefined
        ? fetchPermissionsAdmin(props.organisationId, props.permissionSets)
        : fetchPermissions(props?.permissionSets),
  });

  const getPermission = (
    permissions: PlanPermissions,
    permissionKey: AnyPermissionsKey,
  ): PermissionStatus => {
    const resource = permissions[permissionKey.resource] as Record<
      string,
      PermissionStatus
    >;
    return resource[permissionKey.action];
  };

  /**
   * A utility for calculating if the user is allowed to do something based on the provided permission keys.
   * @param keys the list of permission keys you want to check
   * @returns true/false depending on whether permission is allowed
   */

  const isPermitted = (keys: AnyPermissionsKey[]): boolean => {
    if (!permissionsQuery.isSuccess) {
      // This will probably have strange effects. Its just an escape hatch to prevent a less graceful failure
      return false;
    }

    var isPermissionAllowedAll = keys.map(
      (permissionKey) =>
        getPermission(permissionsQuery.data, permissionKey).allowed,
    );

    const isPermissionAllowed =
      isPermissionAllowedAll.length > 0 &&
      isPermissionAllowedAll.every((val) => val === true);

    return isPermissionAllowed;
  };

  /**
   * A utility for fetching the correct message string to display to the user for why permission was denied.
   *  This is a short message intended to be used on button tooltips, etc. i.e. "You must upgrade to access this feature."
   * @param keys the list of permission keys you want to check
   * @param messages override the default messages with your own custom ones
   */
  const permissionDeniedMessageString = (
    keys: AnyPermissionsKey[],
    messages?: Partial<Record<Reason, String>>,
  ): String => {
    if (!permissionsQuery.isSuccess) {
      return "";
    }
    const permissionIsAllowed = isPermitted(keys);
    const permissionCheckReason = _permissionDeniedReason(keys);
    if (permissionIsAllowed === false && permissionCheckReason === undefined) {
      // TODO DS-5166: This is an error state but should not be possible? :shrug:

      return "";
    } else if (permissionCheckReason === undefined) {
      // This is valid if permission is allowed

      return "";
    }

    // Return the custom message string if it exists, otherwise return the default
    if (messages && messages[permissionCheckReason] !== undefined) {
      return messages[permissionCheckReason]!;
    } else {
      return PermissionReasonMessagesString[permissionCheckReason];
    }
  };

  /**
   * WARNING: This should NOT be displayed to the user!!
   *
   * A utility for calculating the internal reason why a permission was denied.
   *  i.e. upgrade_required, externally_managed, etc
   * @param keys the list of permission keys you want to check
   */
  const _permissionDeniedReason = (keys: AnyPermissionsKey[]) => {
    if (!permissionsQuery.isSuccess) {
      return undefined;
    }
    if (isPermitted(keys)) {
      return undefined;
    }
    var permissionReasonsAll = keys
      .map(
        (permissionKey) =>
          getPermission(permissionsQuery.data, permissionKey).reason,
      )
      .filter((value) => value !== undefined && value !== null);
    permissionReasonsAll.sort((a, b) => {
      const aIndex = Object.values(PermissionsReasons).indexOf(a!);
      const bIndex = Object.values(PermissionsReasons).indexOf(b!);
      return aIndex - bIndex;
    });

    return permissionReasonsAll[0] as Reason;
  };

  /**
   * A utility for fetching the correct message to display to the user for why permission was denied.
   *  This is usually a combination of a text message to explain why permission was denied and a button/action
   *  the user can do to resolve it. i.e. "You need to upgrade" + "Upgrade button"
   * @param keys
   * @param messages
   */
  const _permissionDeniedMessage = (
    keys: AnyPermissionsKey[],
    messages?: Partial<Record<Reason, React.ReactNode>>,
    templateFn?: PermissionMessageTemplateFn,
  ) => {
    if (!permissionsQuery.isSuccess) {
      // Failsafe case. Shouldnt be relied on
      return <></>;
    }

    const highestPriorityReason = _permissionDeniedReason(keys);
    const highestPriorityPermissionKey = _permissionDeniedKey(keys);

    if (
      highestPriorityReason === undefined ||
      highestPriorityPermissionKey === undefined
    ) {
      return <>!!! Something wrong sad !!!</>;
    }

    // Return a custom message if it exists, otherwise return the default one
    if (messages && messages[highestPriorityReason] !== undefined) {
      return messages[highestPriorityReason];
    } else {
      return PermissionReasonMessages[highestPriorityReason](
        highestPriorityPermissionKey,
        templateFn,
      );
    }
  };

  /**
   * Utility function for calculating the first key which causes the highest priority permission denied reason
   * @param keys
   * @returns
   */
  const _permissionDeniedKey = (keys: AnyPermissionsKey[]) => {
    if (!permissionsQuery.isSuccess) {
      return undefined;
    }
    const highestPriorityReason = _permissionDeniedReason(keys);

    const highestPriorityPermissionKey = keys.find(
      (permissionKey) =>
        getPermission(permissionsQuery.data, permissionKey).reason ==
        highestPriorityReason,
    );

    return highestPriorityPermissionKey;
  };

  /**
   * Get the admin override status for a permission key
   * @param key the permission key to check (e.g., "members.create")
   * @returns "allow" | "deny" if overridden by admin, null otherwise
   */
  const getAdminOverride = (
    key: AnyPermissionsKey,
  ): "allow" | "deny" | null => {
    if (!permissionsQuery.isSuccess) {
      return null;
    }
    const permissionKeyString =
      `${key.resource}.${key.action}` as PermissionKeyString;
    return permissionsQuery.data.overrides_applied[permissionKeyString] ?? null;
  };

  return {
    isPermitted,
    permissionDeniedMessageString,
    _permissionDeniedReason,
    _permissionDeniedMessage,
    _permissionDeniedKey,
    getAdminOverride,
    isPermissionsSuccess: permissionsQuery.isSuccess,
    isPermissionsLoading: permissionsQuery.isPending,
  };
};
